/**
 * A semaphore-based concurrency limiter with support for named groups.
 * Each group has its own independent queue and concurrency cap.
 * Calls in one group never block calls in another group.
 */
export class ConcurrencyLimiter {
  private maxConcurrent: number;
  private currentlyRunning: number = 0;
  private queue: Array<() => void> = [];

  constructor(maxConcurrent: number) {
    this.maxConcurrent = maxConcurrent;
  }

  async run<T>(task: () => Promise<T>): Promise<T> {
    await this.acquireSlot();
    try {
      return await task();
    } catch (error) {
      // Ignore the error here, as it will be handled in the next step when checking the response status code.
      throw error;
    } finally {
      this.releaseSlot();
    }
  }

  private acquireSlot(): Promise<void> {
    if (this.currentlyRunning < this.maxConcurrent) {
      this.currentlyRunning++;
      return Promise.resolve();
    }
    return new Promise<void>((resolve) => {
      this.queue.push(resolve);
    });
  }

  private releaseSlot(): void {
    const next = this.queue.shift();
    if (next) {
      next();
    } else {
      this.currentlyRunning--;
    }
  }

  getStats() {
    return {
      running: this.currentlyRunning,
      queued: this.queue.length,
    };
  }
}

/**
 * Registry that manages one ConcurrencyLimiter per action group.
 * Unknown/unmapped actions get an "unlimited" limiter (very high cap)
 * so they are never blocked.
 */
export class ConcurrencyLimiterRegistry {
  // 👇 Define your throttled action groups and their caps here
  private static readonly groupConfig: Record<string, number> = {
    useGetImage: 10,
    MUN_EvaluateSavedQuery: 5,
  };

  private static readonly UNLIMITED = 10_000;
  private static readonly registry = new Map<string, ConcurrencyLimiter>();

  /**
   * Returns the limiter for the given action.
   * - If the action belongs to a configured group → uses that group's limiter.
   * - Otherwise → uses an "unlimited" limiter (never blocks).
   */
  static getLimiter(action: string): ConcurrencyLimiter {
    // Check if this action maps to a known throttled group
    const groupName = this.resolveGroup(action);

    if (!this.registry.has(groupName)) {
      const cap = this.groupConfig[groupName] ?? this.UNLIMITED;
      this.registry.set(groupName, new ConcurrencyLimiter(cap));
    }

    return this.registry.get(groupName)!;
  }

  /**
   * Resolves which group an action belongs to.
   * Extend this logic if one limiter should cover multiple actions.
   */
  private static resolveGroup(action: string): string {
    // Direct match first
    if (action in this.groupConfig) return action;

    // Fallback: no throttling group → unlimited bucket
    return `__unlimited__${action}`;
  }

  /** Debug helper */
  static getStats() {
    const stats: Record<
      string,
      ReturnType<ConcurrencyLimiter["getStats"]>
    > = {};
    this.registry.forEach((limiter, group) => {
      stats[group] = limiter.getStats();
    });
    return stats;
  }
}
